import { ChangeEvent, useContext, useRef } from 'react';

import { DealMediaTypeEnum } from '../../../../../gql/generated/graphql';
import { MediaContext } from '../../../../../hooks/useMediaCollection';
import { isDocumentSatisfied, useDocumentUpload } from '../live/postAcceptance/useDocumentUpload';
import { PhotoEntry } from '../photos/buildPhotoSlotMap';
import { DRIVERS_LICENSE_SLOT } from '../photos/constants';

const usePrelistLicenseCapture = (dealId: string) => {
  const { media, deleteImage } = useContext(MediaContext);
  const { statusByType, documents, isProcessing, replaceDocument, removeDocument } =
    useDocumentUpload(dealId);
  const inputRef = useRef<HTMLInputElement>(null);

  const status = statusByType.drivers_license;
  const licenseDocuments = documents.filter(
    (document) => document.documentType === 'drivers_license',
  );
  const newestDocument = [...licenseDocuments].sort(
    (first, second) => Date.parse(second.createdAt) - Date.parse(first.createdAt),
  )[0];
  const onDealUrl = media.find(
    (item) => item.type === DealMediaTypeEnum.FrontOfDriversLicense,
  )?.signed_url;

  const satisfied = isDocumentSatisfied(status);
  const locked = status === 'onDeal';
  const photo: PhotoEntry | undefined = satisfied
    ? {
        slotId: DRIVERS_LICENSE_SLOT.id,
        photoId: newestDocument?.id ?? 'on-deal',
        previewUrl: newestDocument?.url ?? onDealUrl ?? '',
        uploadedAt: 0,
      }
    : undefined;

  const openPicker = () => {
    const input = inputRef.current;
    if (!input) return;
    input.value = '';
    input.click();
  };

  const upload = (file: File) => replaceDocument('drivers_license', file);

  const onFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) await upload(file);
  };

  const remove = async () => {
    const dealCopies = media.filter(
      (item) => item.type === DealMediaTypeEnum.FrontOfDriversLicense,
    );
    await Promise.all([
      ...licenseDocuments.map((document) => removeDocument(document.id)),
      ...dealCopies.map((dealCopy) => deleteImage(dealCopy)),
    ]);
  };

  const busy =
    isProcessing('drivers_license') ||
    licenseDocuments.some((document) => isProcessing(`remove:${document.id}`));

  return { satisfied, locked, photo, busy, inputRef, openPicker, upload, onFileSelected, remove };
};

export default usePrelistLicenseCapture;
